import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  Input,
  Output,
  EventEmitter
} from '@angular/core'
import { Router, NavigationEnd } from '@angular/router'
import { SidenavService } from '../../services/sidenav.service'
import { Subject, takeUntil, filter } from 'rxjs'

import { CommonModule } from '@angular/common'
import { SideNavComponent } from '../side-nav/side-nav.component'

@Component({
  selector: 'app-mobile-sidenav',
  templateUrl: './mobile-sidenav.component.html',
  styleUrls: ['./mobile-sidenav.component.scss'],
  standalone: true,
  imports: [CommonModule, SideNavComponent]
})
export class MobileSidenavComponent implements OnInit, OnDestroy {
  @Input() currentLanguage: string = 'en'
  @Input() isVisible: boolean = false
  @Output() closeMenu = new EventEmitter<void>()

  screenWidth: number = 0
  private destroy$ = new Subject<void>()

  constructor(
    private sidenavService: SidenavService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.screenWidth = window.innerWidth
    this.checkScreenSize()

    // Subscribe to mobile menu state changes
    this.sidenavService.mobileMenuToggle$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        this.isVisible = isOpen && this.isMobileScreen
      })

    // Subscribe to navigation events to auto-close mobile menu
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Auto-close mobile menu after successful navigation
        if (this.isVisible && this.isMobileScreen) {
          this.closeMobileMenu()
        }
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.screenWidth = event.target.innerWidth
    this.checkScreenSize()

    // Auto-close if screen becomes larger
    if (!this.isMobileScreen && this.isVisible) {
      this.closeMobileMenu()
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.isVisible) {
      this.closeMobileMenu()
    }
  }

  private checkScreenSize(): void {
    // Mobile sidenav only shows on screens < 769px
    if (this.screenWidth >= 769 && this.isVisible) {
      this.closeMobileMenu()
    }
  }

  closeMobileMenu(): void {
    this.sidenavService.closeMobileMenu()
    this.closeMenu.emit()
  }

  onMenuItemSelected(): void {
    // This method can be called by child components when a menu item is selected
    // It provides an additional way to close the menu if needed
    if (this.isVisible && this.isMobileScreen) {
      this.closeMobileMenu()
    }
  }

  onBackdropClick(event: Event): void {
    // Close menu when clicking on backdrop
    if (event.target === event.currentTarget) {
      this.closeMobileMenu()
    }
  }

  get isMobileScreen(): boolean {
    return this.screenWidth < 769
  }

  get shouldShow(): boolean {
    return this.isVisible && this.isMobileScreen
  }

  get sidenavClasses(): string[] {
    const classes = ['mobile-sidenav']

    if (this.currentLanguage === 'ar') {
      classes.push('rtl')
    } else {
      classes.push('ltr')
    }

    if (this.shouldShow) {
      classes.push('visible')
    }

    return classes
  }

  get backdropClasses(): string[] {
    const classes = ['mobile-sidenav-backdrop']

    if (this.shouldShow) {
      classes.push('visible')
    }

    return classes
  }
}
